// @migration-in-progress
import React, { useState } from 'react';
import {
  LayoutDashboard,
  FileText,
  AlertTriangle,
  Ship,
  Wrench,
  ShieldAlert,
  AlertCircle,
  Users,
  TrendingUp,
  Settings,
  X,
  Menu,
  Bell,
  ChevronDown,
  Database,
  UserCircle,
  LogOut,
  Palette,
  FlaskConical,
  Anchor,     // Fleet (vessels-only) nav icon — vessels, not facilities
  Shield,     // Admin Panel icon
  Plus,       // FT-57: Vessel Gate Screen
  ArrowLeft,
  HelpCircle  // Help & Guide nav item
} from 'lucide-react';
import { FLAGS } from '../../config/featureFlags';
import { useVessel } from '../../contexts/VesselContext';
import { track1 } from '../../utils/track1Telemetry';
import { usePermissions } from '../../hooks/usePermissions';
import { useCustomDialogs } from '../../hooks/useCustomDialogs';
import { useAuth } from './auth/AuthGate';
import { useLoginComplianceCheck } from '../../hooks/useLoginComplianceCheck';
import TailwindMainContent from './TailwindMainContent';
import VesselSettingsModal from './settings/VesselSettingsModal';
import VesselSettingsView from './settings/VesselSettingsView';
import VesselSelector from './VesselSelector';
import AddVesselModal from './AddVesselModal';
// FT-365 v3 R2 blocker 8: UI for Layer A#1's sr.vessel.showPickerGate flag —
// renders only when the flag is set, otherwise no-ops. Blocks interaction if
// no vessels are available for the account.
import VesselPickerModal from './VesselPickerModal';
// import SettingsModal from '../Settings/SettingsModal'; // TODO: Create or find component
import EmergencyPanel from '../EmergencyPanel';
import EmergencyButton from '../EmergencyButton';
import UnifiedDevPanel from './UnifiedDevPanel';
import { MobileBottomBar } from '../common/MobileBottomBar';
import { SyncStatusIndicator } from '../platform/SyncStatusIndicator';
import { formatUserIdentity, formatRoleLabel } from '../../utils/userDisplay';
// FT-356: shell-level context so any view can register a "back" handler
// that the MobileBottomBar renders as a Back button on the left of the bar.
import { NavigationBackProvider, useBackHandler } from '../../contexts/NavigationBackContext';
// FT-358: shell-level context so any view can register a list of middle-slot
// actions (icon + label) that the MobileBottomBar renders as icon-tabs.
// Nested inside NavigationBackProvider so back+actions share the same shell.
import { MobileBottomBarActionsProvider } from '../../contexts/MobileBottomBarActionsContext';
// FT-357: shell-level swipe-back — reads the current onBack handler from
// NavigationBackContext and fires it on edge-swipe from the left.
import SwipeBackShell from '../common/SwipeBackShell';

const TailwindShellInner = () => {
  // Check for compliance issues on login/load and send email notifications
  useLoginComplianceCheck();

  // FT-443: Post-login landing = the Fleet chooser ('fleet-landing'), not the
  // vessel Dashboard. FleetLanding is an unconditional nav entry (all roles) and
  // is null-safe while the vessel list hydrates (renders the loading/empty state,
  // never white-screens). Tapping a vessel card still routes to 'dashboard', and
  // the 0-vessel welcome gate (see below) still intercepts before this is used.
  const [activeModule, setActiveModule] = useState('fleet-landing');
  const [moduleTab, setModuleTab] = useState(null);
  const { getCurrentVessel, vessels, isHydrating, currentVesselId, setCurrentVessel } = useVessel();
  const { can, getDeniedMessage, isFleet, isSuperAdmin } = usePermissions();
  const { showAlert, AlertDialog } = useCustomDialogs();
  const { user: currentUser, logout, isAuthenticated } = useAuth();
  const desktopOnBack = useBackHandler();
  const currentVessel = getCurrentVessel();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showEmergency, setShowEmergency] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [showVesselSettings, setShowVesselSettings] = useState(false);
  const [showVesselSettingsView, setShowVesselSettingsView] = useState(false);
  const [showAddVessel, setShowAddVessel] = useState(false);

  // Full-screen voyage mode - hides app header and bottom nav on mobile
  // Lifted from OperationsModule so TailwindShell can hide chrome
  const [voyageFullScreen, setVoyageFullScreen] = useState(false);

  // Function to set module with optional tab
  const setActiveModuleWithTab = (module, tab = null) => {
    setActiveModule(module);
    setModuleTab(tab);
    // Store tab preference for the module to pick up
    if (tab) {
      sessionStorage.setItem(`moduleTab_${module}`, tab);
      // Dispatch event for same-module navigation
      window.dispatchEvent(new CustomEvent('maintenance:switchTab', { detail: { tab } }));
    }
  };
  
  // Check for vessel settings navigation from dashboard
  React.useEffect(() => {
    const openVesselSettings = sessionStorage.getItem('openVesselSettings');
    if (openVesselSettings === 'true') {
      setShowVesselSettingsView(true);
      sessionStorage.removeItem('openVesselSettings');
    }
  }, [activeModule]);

  // Listen for sidebar open events from MobileBottomBar
  React.useEffect(() => {
    const handleOpenSidebar = () => setSidebarOpen(true);
    window.addEventListener('shell:openSidebar', handleOpenSidebar);
    return () => window.removeEventListener('shell:openSidebar', handleOpenSidebar);
  }, []);

  // Navigation configuration - Main categories only
  const navigation = [
    // FT-436 (VESSELS-ONLY): photo-card Fleet landing. Available to every role —
    // it renders only the vessels the server already scoped to this user
    // (FT-435), and tapping a card enters that vessel's lane.
    { id: 'fleet-landing', label: 'Fleet', icon: Anchor },
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { id: 'operations', label: 'Operations', icon: Ship },
    { id: 'crew', label: 'Crew', icon: Users },
    { id: 'maintenance', label: 'Equipment', icon: Wrench },
    { id: 'defects', label: 'Defects', icon: AlertTriangle },
    { id: 'drills', label: 'Drills', icon: ShieldAlert },
    { id: 'risk', label: 'Risk Assessments', icon: AlertTriangle },
    { id: 'incidents', label: 'Incidents', icon: AlertCircle },
    { id: 'policies', label: 'Docs & Compliance', icon: FileText },  // FT-122: Renamed from 'Policies & Procedures'
    // FT-257: Reports & Review hidden until module is properly built
    // { id: 'reports', label: 'Reports & Review', icon: TrendingUp },
    { id: 'account', label: 'Account', icon: UserCircle },
    // FT-256: Fleet Dashboard hidden until module is properly built — FT-513 will revisit
    // ...(isFleet ? [{ id: 'fleet', label: 'Fleet Dashboard', icon: Building2 }] : []),
    // FT-104: Hidden - overlaps with Crew module and Team tab (Account > Team)
    // ...(isFleet ? [{ id: 'client-admin', label: 'Manage Crew', icon: Users }] : []),
    { id: 'help', label: 'Help & Guide', icon: HelpCircle },
    // Admin Panel - only visible to super_admin
    ...(isSuperAdmin ? [{ id: 'admin', label: 'Admin Panel', icon: Shield }] : [])
  ];

  const handleNavigation = async (moduleId) => {
    // Check permissions for restricted modules
    if (moduleId === 'settings') {
      // Check if user can update vessel settings
      if (!can('update', 'vessel')) {
        await showAlert(
          'Permission Denied',
          'Only Fleet administrators and Skippers can access vessel settings.',
          'error'
        );
        return;
      }
      setShowVesselSettingsView(true);
      setSidebarOpen(false);
      return;
    }
    
    // Check drill settings permission
    if (moduleId === 'drills') {
      // Crew can view drills but not access settings
      const canManageDrills = can('update', 'drills');
      // Store this for the drills module to check
      sessionStorage.setItem('canManageDrills', canManageDrills.toString());
    }
    
    if (moduleId === 'emergency') {
      setShowEmergency(true);
      setSidebarOpen(false);
      return;
    }
    
    setActiveModule(moduleId);
    setSidebarOpen(false);
  };

  const NavigationItem = ({ item }) => {
    const Icon = item.icon;
    const isActive = activeModule === item.id;

    return (
      <li>
        <button
          onClick={() => handleNavigation(item.id)}
          className={`
            group flex gap-x-3 rounded-md p-3 lg:p-2 text-base lg:text-sm font-semibold w-full text-left min-h-[48px] lg:min-h-[44px] items-center
            ${isActive
              ? 'bg-white/10 text-white'
              : 'text-gray-400 hover:text-white hover:bg-white/5'
            }
          `}
        >
          <Icon className={`h-6 w-6 shrink-0 ${item.badge ? 'text-cyan-400' : ''}`} />
          <span className="flex-1">{item.label}</span>
          {item.badge && (
            <span className="ml-2 px-1.5 py-0.5 text-xs font-medium bg-cyan-600 text-white rounded">
              {item.badge}
            </span>
          )}
        </button>
      </li>
    );
  };

  // FT-354d: Mobile menu tile — tap to navigate. Favourites feature removed (FT-355
  // storage-layer interaction was broken on staging). 3-column grid layout preserved.
  const MobileMenuTile = ({ item }) => {
    const Icon = item.icon;
    const isActive = activeModule === item.id;

    return (
      <button
        type="button"
        onClick={() => handleNavigation(item.id)}
        aria-label={item.label}
        aria-pressed={isActive}
        data-testid={`mobile-menu-tile-${item.id}`}
        className={`
          relative flex flex-col items-center justify-center gap-1 h-24 rounded-xl border transition-colors select-none
          ${isActive
            ? 'bg-gray-700 border-gray-600 text-white'
            : 'bg-gray-800 border-gray-800 text-gray-300 hover:bg-gray-700 active:bg-gray-700'
          }
        `}
      >
        <Icon className="h-6 w-6 shrink-0" />
        <span className="text-xs text-center px-1 leading-tight">{item.label}</span>
      </button>
    );
  };

  // Show full-screen vessel settings if active
  if (showVesselSettingsView) {
    return (
      <VesselSettingsView
        onBack={() => setShowVesselSettingsView(false)}
      />
    );
  }

  // FT-57 / FT-365 v3.5: Loading state - prevent flash while vessels is
  // undefined OR during initial auth-hydration. isHydrating covers the
  // account-change window between Layer B's wipeUserCaches (which correctly
  // removes the `vessels` LS key) and getVessels() resolving — without it,
  // vessels transiently reads as [] and the welcome gate flashes for ~200-800ms.
  if (vessels === undefined || vessels === null || isHydrating) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800 flex flex-col items-center justify-center px-4">
        <img
          src="/fleetskipper-logo.png"
          alt="FleetSkipper"
          className="h-16 w-auto brightness-0 invert mb-8"
        />
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
        <p className="text-slate-400 mt-4 text-sm">Loading...</p>
      </div>
    );
  }

  // FT-57: Vessel Gate Screen - Block app access until user creates a vessel
  // This prevents client-only UUID sync issues by forcing server registration
  if (vessels.length === 0) {
    // FT-365 Option B Phase 2: welcome-gate render observability. Fires every
    // render of the empty-vessels welcome gate — sudden bursts on account-
    // change signal that Option B's reload path is failing and users are
    // seeing a stale post-wipe empty state before getVessels() lands.
    try {
      track1.log('[WELCOME-GATE-RENDER]', {
        vesselsLen: vessels?.length ?? 0,
        isHydrating,
        currentVesselId: currentVesselId ?? null,
      });
    } catch (_) { /* non-fatal */ }

    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800 flex flex-col items-center justify-center px-4">
        {/* Logo */}
        <img
          src="/fleetskipper-logo.png"
          alt="FleetSkipper"
          className="h-16 w-auto brightness-0 invert mb-8"
        />

        {/* Welcome Card */}
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-cyan-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Ship className="h-8 w-8 text-cyan-600" />
          </div>

          <h1 className="text-2xl font-bold text-slate-900 mb-2">
            Welcome to FleetSkipper
          </h1>

          <p className="text-slate-600 mb-6">
            Get started by adding your first vessel. This will set up your Safety Management System.
          </p>

          <button
            onClick={() => setShowAddVessel(true)}
            className="w-full inline-flex items-center justify-center px-6 py-3 bg-cyan-600 hover:bg-cyan-700 text-white font-medium rounded-lg transition-colors"
          >
            <Plus className="h-5 w-5 mr-2" />
            Add Your First Vessel
          </button>

          <p className="text-xs text-slate-400 mt-6">
            Your vessel data will be synced securely to the cloud.
          </p>
        </div>

        {/* Sign out link - escape hatch if server is down */}
        {isAuthenticated && (
          <button
            onClick={() => {
              if (window.confirm('Are you sure you want to sign out?')) {
                logout();
              }
            }}
            className="mt-6 text-slate-400 hover:text-white text-sm flex items-center gap-2 transition-colors"
          >
            <LogOut className="h-4 w-4" />
            Sign out
          </button>
        )}

        {/* Add Vessel Modal - opens when button clicked */}
        <AddVesselModal
          isOpen={showAddVessel}
          onClose={() => setShowAddVessel(false)}
        />
      </div>
    );
  }

  return (
    <>
      {/* FT-365 v3 R2 blocker 8: renders only when sr.vessel.showPickerGate is
          set. Mounted at the top of the shell so it overlays all other UI. */}
      <VesselPickerModal />

      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-gray-900/80 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Mobile sidebar — full-screen on mobile for easy tap targets */}
      <div className={`
        fixed inset-y-0 left-0 z-50 w-full sm:w-80 bg-gray-900 transform transition-transform duration-300 ease-in-out lg:hidden
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className="flex h-full flex-col">
          {/* Header with logo and close button */}
          <div className="flex h-20 items-center justify-between px-6">
            <img
              src="/fleetskipper-logo.png"
              alt="FleetSkipper"
              className="h-12 w-auto brightness-0 invert"
            />
            <button
              type="button"
              className="-m-2.5 p-2.5 text-white"
              onClick={() => setSidebarOpen(false)}
            >
              <span className="sr-only">Close sidebar</span>
              <X className="h-7 w-7" />
            </button>
          </div>

          {/* Mobile navigation — FT-354d: 3-col button grid (favourites feature removed). */}
          <nav
            className="flex flex-1 flex-col pb-4 overflow-y-auto"
            data-testid="mobile-menu-nav"
          >
            <ul role="list" className="flex flex-1 flex-col gap-y-4">
              <li>
                <div className="grid grid-cols-3 gap-3 px-4 isolate" data-testid="mobile-menu-grid"> {/* FT-360: isolate — force compositor to rasterize grid separately; Adreno GPU can't blend semi-transparent tile bg inside transform parent */}
                  {navigation.map((item) => (
                    <MobileMenuTile key={item.id} item={item} />
                  ))}
                </div>
              </li>
              <li className="mt-auto space-y-1 px-4">
                <button
                  onClick={() => handleNavigation('emergency')}
                  className="group flex gap-x-3 rounded-md p-2 text-sm font-semibold w-full text-left bg-red-600 text-white hover:bg-red-700 min-h-[44px] items-center"
                >
                  <ShieldAlert className="h-6 w-6 shrink-0" />
                  Emergency
                </button>
                {/* UI Playground - DEV ONLY */}
                {FLAGS.UI_PLAYGROUND && (
                  <button
                    onClick={() => handleNavigation('ui-playground')}
                    className="group flex gap-x-3 rounded-md p-2 text-sm font-semibold w-full text-left text-purple-400 hover:text-purple-300 hover:bg-purple-900/20 min-h-[44px] items-center"
                  >
                    <Palette className="h-6 w-6 shrink-0" />
                    UI Playground
                  </button>
                )}
                {/* Design Lab - DEV ONLY */}
                {FLAGS.DESIGN_LAB && (
                  <button
                    onClick={() => handleNavigation('design-lab')}
                    className="group flex gap-x-3 rounded-md p-2 text-sm font-semibold w-full text-left text-cyan-400 hover:text-cyan-300 hover:bg-cyan-900/20 min-h-[44px] items-center"
                  >
                    <FlaskConical className="h-6 w-6 shrink-0" />
                    Design Lab
                  </button>
                )}
                <button
                  onClick={() => handleNavigation('settings')}
                  className="group flex gap-x-3 rounded-md p-2 text-sm font-semibold w-full text-left text-gray-400 hover:text-white hover:bg-white/5 min-h-[44px] items-center"
                >
                  <Settings className="h-6 w-6 shrink-0" />
                  Settings
                </button>
              </li>
            </ul>
          </nav>
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:w-72 lg:flex-col">
        <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-gray-900 px-6 pb-4">
          <div className="flex h-24 shrink-0 items-center">
            <img 
              src="/fleetskipper-logo.png" 
              alt="FleetSkipper" 
              className="h-14 w-auto brightness-0 invert"
            />
          </div>
          <nav className="flex flex-1 flex-col">
            <ul role="list" className="flex flex-1 flex-col gap-y-7">
              <li>
                <ul role="list" className="-mx-2 space-y-1">
                  {navigation.map((item) => (
                    <NavigationItem key={item.id} item={item} />
                  ))}
                </ul>
              </li>
              <li className="mt-auto space-y-1">
                <button
                  onClick={() => handleNavigation('emergency')}
                  className="group flex gap-x-3 rounded-md p-2 text-sm font-semibold w-full text-left bg-red-600 text-white hover:bg-red-700 min-h-[44px] items-center"
                >
                  <ShieldAlert className="h-6 w-6 shrink-0" />
                  Emergency
                </button>
                {/* UI Playground - DEV ONLY */}
                {FLAGS.UI_PLAYGROUND && (
                  <button
                    onClick={() => handleNavigation('ui-playground')}
                    className="group flex gap-x-3 rounded-md p-2 text-sm font-semibold w-full text-left text-purple-400 hover:text-purple-300 hover:bg-purple-900/20 min-h-[44px] items-center"
                  >
                    <Palette className="h-6 w-6 shrink-0" />
                    UI Playground
                  </button>
                )}
                {/* Design Lab - DEV ONLY */}
                {FLAGS.DESIGN_LAB && (
                  <button
                    onClick={() => handleNavigation('design-lab')}
                    className="group flex gap-x-3 rounded-md p-2 text-sm font-semibold w-full text-left text-cyan-400 hover:text-cyan-300 hover:bg-cyan-900/20 min-h-[44px] items-center"
                  >
                    <FlaskConical className="h-6 w-6 shrink-0" />
                    Design Lab
                  </button>
                )}
                <button
                  onClick={() => handleNavigation('settings')}
                  className="group flex gap-x-3 rounded-md p-2 text-sm font-semibold w-full text-left text-gray-400 hover:text-white hover:bg-white/5 min-h-[44px] items-center"
                >
                  <Settings className="h-6 w-6 shrink-0" />
                  Settings
                </button>
              </li>
            </ul>
          </nav>
        </div>
      </div>

      {/* Main content area */}
      <div className="lg:pl-72 min-h-screen">
        {/* Header - Mobile matches sidebar colors, Desktop stays white */}
        {/* Hidden on mobile when voyageFullScreen is active */}
        <div className={`sticky top-0 z-40 flex h-20 sm:h-16 shrink-0 items-center justify-between border-b border-gray-200 bg-gray-900 lg:bg-white px-4 shadow-sm sm:px-6 lg:px-8 ${voyageFullScreen ? 'hidden lg:flex' : ''}`}>
          {/* Mobile/Tablet Layout (< lg) */}
          <div className="flex items-center justify-between w-full lg:hidden">
            {/* Left side: Vessel selector (hamburger removed — Menu button in bottom bar) */}
            <div className="flex items-center gap-x-2">
              <div className="w-auto">
                <VesselSelector
                  onAddVessel={() => setShowAddVessel(true)}
                  onFleetOverview={() => setActiveModule('dashboard')}
                />
              </div>
            </div>

            {/* Center: Logo */}
            <div className="absolute left-1/2 transform -translate-x-1/2">
              <img 
                src="/fleetskipper-logo.png" 
                alt="FleetSkipper" 
                className="h-10 sm:h-8 w-auto brightness-0 invert"
              />
            </div>

            {/* Right side: Notifications + Profile */}
            <div className="flex items-center gap-x-2">
              {/* FT-577: mobile had NO sync badge. Shown ONLY when an upload
                  failed (renders nothing otherwise), so a lost photo can't go
                  unnoticed on the phone where most photos are taken. */}
              <SyncStatusIndicator attentionOnly />
              <button type="button" className="-m-2.5 p-2.5 text-white hover:text-gray-300">
                <span className="sr-only">View notifications</span>
                <Bell className="h-6 w-6" />
              </button>
              
              {/* Profile dropdown */}
              <div className="relative">
                <button
                  type="button"
                  onClick={() => {
                    console.log('[PROFILE] Toggling menu (mobile), current state:', profileMenuOpen);
                    setProfileMenuOpen(!profileMenuOpen);
                  }}
                  className="flex items-center -m-1.5 p-1.5"
                >
                  <span className="sr-only">Open user menu</span>
                  <div className="h-8 w-8 rounded-full bg-gray-300 border-2 border-white flex items-center justify-center">
                    <span className="text-sm font-medium text-gray-700">U</span>
                  </div>
                </button>
              </div>
            </div>
          </div>

          {/* Desktop Layout (lg+) - Keep existing */}
          <div className="hidden lg:flex lg:items-center lg:gap-x-4 lg:w-full">
            {/* Desktop back button — reads from NavigationBackContext, mirrors MobileBottomBar on desktop */}
            {desktopOnBack && (
              <button
                onClick={desktopOnBack}
                className="flex items-center gap-1.5 text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors shrink-0"
              >
                <ArrowLeft className="h-4 w-4" />
                Back
              </button>
            )}
            {/* Vessel selector */}
            <div className="flex items-center flex-1">
              <VesselSelector 
                onAddVessel={() => setShowAddVessel(true)}
                onFleetOverview={() => setActiveModule('dashboard')}
              />
            </div>

            <div className="flex items-center gap-x-4 lg:gap-x-6">
              {/* Phase 6: Sync Status Indicator */}
              <SyncStatusIndicator />

              {/* Separator */}
              <div className="h-6 w-px bg-gray-900/10" />

              <button type="button" className="-m-2.5 p-2.5 text-gray-400 hover:text-gray-500">
                <span className="sr-only">View notifications</span>
                <Bell className="h-6 w-6" />
              </button>

              {/* Separator */}
              <div className="h-6 w-px bg-gray-900/10" />

              {/* Profile dropdown - Desktop */}
              <div className="relative">
                <button
                  type="button"
                  onClick={() => {
                    console.log('[PROFILE] Toggling menu (desktop), current state:', profileMenuOpen);
                    setProfileMenuOpen(!profileMenuOpen);
                  }}
                  className="flex items-center -m-1.5 p-1.5"
                >
                  <span className="sr-only">Open user menu</span>
                  <div className="h-8 w-8 rounded-full bg-gray-300 flex items-center justify-center">
                    <span className="text-sm font-medium text-gray-700">
                      {(currentUser?.name || currentUser?.username || 'U').charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <span className="flex items-center">
                    <span className="ml-4 text-sm font-semibold text-gray-900">
                      {/* FT-06e: Show "Name (Role)" format */}
                      {formatUserIdentity(currentUser)}
                    </span>
                    <ChevronDown className="ml-2 h-5 w-5 text-gray-400" />
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Profile dropdown menu - shared between mobile and desktop */}
        {profileMenuOpen && (
          <>
            <div
              className="fixed inset-0 z-[9998]"
              onClick={() => setProfileMenuOpen(false)}
            />
            <div className="absolute right-4 sm:right-6 lg:right-8 top-16 sm:top-14 lg:top-14 w-56 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 z-[9999]">
              {/* User info header - FT-06e: Show name and formatted role */}
              {currentUser && (
                <div className="px-4 py-3 border-b border-gray-200">
                  <p className="text-sm font-medium text-gray-900">{currentUser.name || currentUser.username}</p>
                  <p className="text-xs text-gray-500 mt-1">{currentUser.email}</p>
                  <p className="text-xs text-cyan-600 mt-1 font-medium">{formatRoleLabel(currentUser.role)}</p>
                </div>
              )}

              <button
                onClick={() => {
                  setShowVesselSettingsView(true);
                  setProfileMenuOpen(false);
                }}
                className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
              >
                <Ship className="inline-block w-4 h-4 mr-2" />
                Vessel Settings
              </button>

              <button
                onClick={() => {
                  setActiveModule('account');
                  setProfileMenuOpen(false);
                }}
                className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
              >
                <UserCircle className="inline-block w-4 h-4 mr-2" />
                Your Profile
              </button>

              {isAuthenticated && (
                <button
                  onClick={() => {
                    if (window.confirm('Are you sure you want to sign out?')) {
                      logout();
                      setProfileMenuOpen(false);
                    }
                  }}
                  className="block w-full text-left px-4 py-2 text-sm text-red-700 hover:bg-red-50 border-t border-gray-200"
                >
                  <LogOut className="inline-block w-4 h-4 mr-2" />
                  Sign out
                </button>
              )}
            </div>
          </>
        )}

        {/* Page content */}
        {/* voyageFullScreen only affects mobile layout - desktop keeps normal padding */}
        <main className={`pb-10 overflow-x-hidden ${voyageFullScreen ? 'pt-0 sm:pt-8' : 'pt-8'}`}>
          <div className={`max-w-full overflow-x-hidden ${voyageFullScreen ? 'px-0 sm:px-6 lg:px-8' : 'px-4 sm:px-6 lg:px-8'}`}>
            <TailwindMainContent
              activeModule={activeModule}
              setActiveModule={setActiveModule}
              setActiveModuleWithTab={setActiveModuleWithTab}
              onOpenVesselSettings={() => setShowVesselSettingsView(true)}
              onSelectFleetVessel={(vesselId) => {
                // FT-436: enter the tapped vessel's lane exactly as VesselSelector
                // does (setCurrentVessel) then land on its dashboard.
                setCurrentVessel(vesselId);
                setActiveModule('dashboard');
              }}
              voyageFullScreen={voyageFullScreen}
              setVoyageFullScreen={setVoyageFullScreen}
            />
          </div>
        </main>
      </div>

      {/* Modals */}
      <VesselSettingsModal
        isOpen={showVesselSettings}
        onClose={() => setShowVesselSettings(false)}
      />
      
      <AddVesselModal
        isOpen={showAddVessel}
        onClose={() => setShowAddVessel(false)}
      />
      
      {/* TODO: Restore when components are available
      <SettingsModal 
        isOpen={showSettings}
        onClose={() => setShowSettings(false)}
      />
      */}
      
      {/* Emergency Panel - vessel-specific checklists */}
      <EmergencyPanel
        isOpen={showEmergency}
        onClose={() => setShowEmergency(false)}
      />

      {/* Default Mobile Bottom Bar - shows Menu button on all pages */}
      {/* Modules with their own MobileBottomBar will render on top of this */}
      {/* Hidden when voyageFullScreen is active - FloatingActionBar takes over */}
      {!voyageFullScreen && <MobileBottomBar />}

      {/* Floating Emergency Button - always visible */}
      <EmergencyButton onClick={() => setShowEmergency(true)} />

      {/* Unified Developer Panel - only in development */}
      <UnifiedDevPanel />
    </>
  );
};

// FT-356: wrap the shell with NavigationBackProvider so every view mounted
// inside can register a "back" handler picked up by MobileBottomBar.
// FT-358: MobileBottomBarActionsProvider nested INSIDE NavigationBackProvider
// (both are shell-wide contexts read by the same MobileBottomBar). Modules
// register their middle-slot actions via `useSetBottomBarActions([...])`.
// FT-357: SwipeBackShell wraps the inner shell so edge-swipe-from-left
// mobile gestures fire the same handler as the Back button. Placed INSIDE
// both providers so it can `useBackHandler()` and children can register
// bottom-bar actions.
const TailwindShell = () => (
  <NavigationBackProvider>
    <MobileBottomBarActionsProvider>
      <SwipeBackShell>
        <TailwindShellInner />
      </SwipeBackShell>
    </MobileBottomBarActionsProvider>
  </NavigationBackProvider>
);

export default TailwindShell;